/* eslint-disable complexity */
import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { FilterSection } from "@faire/retailer-visitor-shared/components/Filters/__internal__/FilterSection";
import { FilterBannerContent } from "@faire/retailer-visitor-shared/components/Filters/FilterBannerContent";
import {
  getFirstFilterSectionsWithTooltip,
  getSortedFilterSections,
  isDefaultShowAllFilterSection,
  syncToggleFilters,
  updateAllFilterKeys,
} from "@faire/retailer-visitor-shared/components/Filters/FilterUtils";
import { ToggleFilterRow } from "@faire/retailer-visitor-shared/components/Filters/ToggleFilterRow";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useStickyNavContentPositions } from "@faire/retailer-visitor-shared/navbar/useStickyNavContentPositions";
import { useRecordMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { AlertBanner } from "@faire/web--source/ui/AlertBanner";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionCategoryOther/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { trackRetailersFilterClickedClick } from "@faire/web-api--source/events/retailers/click/filterClicked";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import type { IUserMilestone } from "@faire/web-api--source/indigofair/data/IUserMilestone";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";

type FilterSectionState = "allExpanded" | "allCollapsed";

export interface IProps {
  name: string;
  filterSectionState?: FilterSectionState;
  className?: string;
  headerSeparator?: boolean;
  isModal?: boolean;
  isSticky?: boolean;
  isLoading?: boolean;
  PageActions?: React.ReactNode;
  children?: React.ReactNode | undefined;
  showUnformattedResultsCount?: boolean;
}

const FilterBarBase: React.FC<
  IProps & { shouldRetainOriginalPosition: boolean }
> = ({
  name,
  filterSectionState = "allExpanded",
  className,
  isModal,
  headerSeparator,
  isSticky = false,
  isLoading,
  PageActions,
  shouldRetainOriginalPosition,
  children,
  showUnformattedResultsCount = false,
}) => {
  const {
    data: {
      filterSections,
      activeFilterCount,
      filterBarScrollTop,
      preserveScrollPosition,
    },
    setFilterState,
    clearAllFilters,
  } = useFilterStore([
    "filterSections",
    "activeFilterCount",
    "isSideBarVisible",
    "filterBarScrollTop",
    "preserveScrollPosition",
  ]);

  const { isDesktopAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const stickyContentRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [scrollContainerScrollTop, setScrollContainerScrollTop] =
    React.useState<number>(
      preserveScrollPosition && filterBarScrollTop !== undefined
        ? filterBarScrollTop
        : 0
    );

  const [sectionTooltipBannerDismissed, setSectionTooltipBannerDismissed] =
    useState<boolean>(false);

  const { heightWithoutPromoBannerExceptMarket: topOfPageHeight } =
    useTopOfPageContent();

  const recordMilestone = useRecordMilestone();

  const sortedFilterSections = React.useMemo(
    () => getSortedFilterSections(filterSections, isModal),
    [filterSections, isModal]
  );

  const tooltipSectionRef = React.useRef<HTMLDivElement>(null);
  const sectionWithTooltip = React.useMemo(
    () => getFirstFilterSectionsWithTooltip(filterSections),
    [filterSections]
  );
  const tooltip = isModal ? undefined : sectionWithTooltip?.tooltip;
  const tooltipRef = useRef(tooltip);
  tooltipRef.current = tooltip;

  const updateFilterBarScrollTop = useCallback(() => {
    setFilterState((currentState) => ({
      ...currentState,
      filterBarScrollTop: scrollContainerRef.current?.scrollTop ?? 0,
    }));
  }, [setFilterState]);

  const debouncedUpdateFilterBarScrollTop = useMemo(
    () => debounceIfPossible(updateFilterBarScrollTop, 500),
    [updateFilterBarScrollTop]
  );

  const hasMounted = useRef(false);
  useLayoutEffect(() => {
    if (
      preserveScrollPosition &&
      !hasMounted.current &&
      scrollContainerRef.current
    ) {
      hasMounted.current = true;
      scrollContainerRef.current?.scrollTo?.({
        top: filterBarScrollTop,
      });
    }
  }, [preserveScrollPosition, filterBarScrollTop]);

  const handleFilterSelected = useCallback(
    (section: IFilterSection, filterKey: string, value: boolean) => {
      trackRetailersFilterClickedClick(name, filterKey);
      const tooltip = tooltipRef.current;
      const sectionsUpdated = updateAllFilterKeys(
        filterSections,
        filterKey,
        value
      );
      syncToggleFilters(section, sectionsUpdated);
      setFilterState((currentState) => ({
        ...currentState,
        filterSections: [...sectionsUpdated],
        isNewFilterSelected: true,
      }));

      if (section.tooltip === tooltip) {
        setSectionTooltipBannerDismissed(true);
      }
    },
    [filterSections, name, setFilterState]
  );

  const handleMilestone = useCallback(
    (milestone: IUserMilestone | undefined) => {
      if (!milestone || !milestone.retailer_milestone) {
        return;
      }
      recordMilestone(milestone.retailer_milestone);
    },
    [recordMilestone]
  );

  const handleCloseTooltip = useCallback(() => {
    handleMilestone(tooltip?.dismiss_milestone);
    setSectionTooltipBannerDismissed(true);
  }, [handleMilestone, tooltip?.dismiss_milestone]);

  const handleScrollToSection = useCallback(() => {
    const top = tooltipSectionRef?.current?.offsetTop
      ? tooltipSectionRef?.current?.offsetTop - 16
      : 0;
    scrollContainerRef?.current?.scrollTo?.({ top, behavior: "smooth" });
    if (preserveScrollPosition) {
      debouncedUpdateFilterBarScrollTop();
    }
    setTimeoutIfPossible(handleCloseTooltip, 2000);
  }, [
    debouncedUpdateFilterBarScrollTop,
    handleCloseTooltip,
    preserveScrollPosition,
  ]);

  const handleClearFilters = useCallback(() => {
    if (activeFilterCount > 0) {
      const actionName = isModal ? "modal_clear_all" : "sidebar_clear_all";
      trackRetailersFilterClearClick(name, actionName, "");
      clearAllFilters();
      scrollContainerRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      if (preserveScrollPosition) {
        debouncedUpdateFilterBarScrollTop();
      }
    }
  }, [
    activeFilterCount,
    clearAllFilters,
    debouncedUpdateFilterBarScrollTop,
    isModal,
    name,
    preserveScrollPosition,
  ]);

  const viewRef = useDoActionWhenVisible(() => {
    trackRetailersFilterInteraction(
      name,
      "tooltip_view",
      sectionWithTooltip?.field_name ?? ""
    );
    handleMilestone(tooltip?.seen_milestone);
  });

  const header = (
    <ColumnWithPseudoElement ref={stickyContentRef}>
      <HeaderContainer
        role="heading"
        aria-level={2}
        $useUpdatedSpacing={isLoggedInRetailer()}
      >
        <Typography
          data-test-id="filter-bar-title"
          variant={
            isLoggedInRetailer()
              ? "paragraphSansMedium"
              : "subheadingSansMedium"
          }
        >
          {activeFilterCount > 0 ? (
            <StrictLocalMsg
              defaultMessage="{activeFilterNumber, plural, one{# filter applied} other{# filters applied}}"
              description={{
                text: "Show the number of active filters to the user",
              }}
              values={{ activeFilterNumber: activeFilterCount }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Filters"
              description={{
                text: "Title for the filter sidebar",
              }}
            />
          )}
        </Typography>
        <ClearAllColumn $useUpdatedSpacing={isLoggedInRetailer()}>
          {activeFilterCount > 0 ? (
            <Button
              variant="tertiary"
              onClick={handleClearFilters}
              data-test-id="filter-sidebar-clean-all"
            >
              <StrictLocalMsg
                defaultMessage="Clear all"
                description={{ text: "Remove all applied filters" }}
              />
            </Button>
          ) : null}
        </ClearAllColumn>
      </HeaderContainer>
      {headerSeparator || scrollContainerScrollTop > 0 ? (
        <>
          <Spacer height={isModal ? "3x" : "2x"} />
          <HorizontalLine />
        </>
      ) : null}
    </ColumnWithPseudoElement>
  );

  const filteredSectionsMemoed = useMemo(
    () =>
      sortedFilterSections.length === 0 && isLoading
        ? // Show 6 placeholders
          Array.from({ length: 6 }, (_value, index) => (
            <React.Fragment key={`loadingPlaceholder_${index}`}>
              <FilterSection
                name={name}
                section={IFilterSection.build()}
                onChange={handleFilterSelected}
                initiallyCollapsed
                showLoading
              />
              {index !== 5 && <HorizontalLine />}
            </React.Fragment>
          ))
        : sortedFilterSections.map((filterSection, index) => {
            if (
              filterSection.selection_type ===
              IFilterSection.SelectionType.TOGGLE
            ) {
              return null;
            }
            const collapsed = filterSectionState === "allCollapsed";
            const showAll = isDefaultShowAllFilterSection(filterSection);

            return (
              <React.Fragment
                key={`${filterSection.field_name}-${filterSection.display_name}`}
              >
                <FilterSection
                  name={name}
                  section={filterSection}
                  onChange={handleFilterSelected}
                  initiallyCollapsed={collapsed}
                  showAll={showAll}
                  divRef={
                    tooltip === filterSection.tooltip
                      ? tooltipSectionRef
                      : undefined
                  }
                  showUnformattedResultsCount={showUnformattedResultsCount}
                />
                {index !== sortedFilterSections.length - 1 && (
                  <HorizontalLine />
                )}
              </React.Fragment>
            );
          }),
    [
      filterSectionState,
      handleFilterSelected,
      isLoading,
      name,
      showUnformattedResultsCount,
      sortedFilterSections,
      tooltip,
    ]
  );

  if (sortedFilterSections.length === 0 && !isLoading && isModal) {
    return (
      <Wrapper $isModal={isModal} align="center" justify="center">
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            defaultMessage="Your search did not match any products"
            description={{
              text: "Texts describe that user's search query doesn't match any product results",
            }}
          />
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      $isModal={isModal}
      className={className}
      data-test-id="filter-bar"
      style={
        isSticky
          ? {
              height: "calc(100vh - " + (topOfPageHeight + 16) + "px)",
              position: "sticky",
              top:
                isLoggedInRetailer() &&
                isDesktopAndAbove &&
                !shouldRetainOriginalPosition
                  ? 16
                  : topOfPageHeight + 16 + "px",
            }
          : undefined
      }
      onWheel={(e) => {
        // preventing the window scroll event from triggering
        e.stopPropagation();
      }}
    >
      {header}
      <ScrollContainer
        ref={scrollContainerRef}
        onScroll={(e: React.UIEvent<HTMLDivElement>) => {
          if (
            (scrollContainerScrollTop === 0 &&
              e.currentTarget.scrollTop !== 0) ||
            (scrollContainerScrollTop !== 0 && e.currentTarget.scrollTop === 0)
          ) {
            setScrollContainerScrollTop(e.currentTarget.scrollTop);
          }
          if (preserveScrollPosition) {
            debouncedUpdateFilterBarScrollTop();
          }
        }}
      >
        {!sectionTooltipBannerDismissed && tooltip && tooltip.message ? (
          <div ref={viewRef}>
            <StyledAlertBanner
              message={
                <FilterBannerContent
                  title={tooltip.title}
                  message={tooltip.message}
                  ctaText={strictLocalize({
                    defaultMessage: "Apply filter",
                    description: {
                      text: "Move the scroll to filter position to apply the specific filter",
                    },
                  })}
                  ctaOnClick={handleScrollToSection}
                />
              }
              onClose={handleCloseTooltip}
            />
          </div>
        ) : null}
        {isModal ? <StyledToggleFilterRow name={name} wrap /> : null}
        {children}
        {filteredSectionsMemoed}
        {PageActions}
        <Spacer height={{ mobileAndAbove: 0, desktopAndAbove: "9x" }} />
      </ScrollContainer>
    </Wrapper>
  );
};

export const FilterBar = (props: IProps) => {
  const { shouldRetainOriginalPosition } = useStickyNavContentPositions();
  return (
    <MemoizedFilterBarBase
      shouldRetainOriginalPosition={shouldRetainOriginalPosition}
      {...props}
    />
  );
};

const MemoizedFilterBarBase = React.memo(FilterBarBase);

const Wrapper = styled(Column)<{ $isModal: boolean | undefined }>`
  z-index: 4;
  width: 100%;

  ${responsiveMediaQueries.tabletAndBelow} {
    height: 100%;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    ${() =>
      isLoggedInRetailer()
        ? "transition: top 300ms cubic-bezier(0.175, 0.735, 0.680, 0.875);"
        : undefined};
  }

  ${responsiveMediaQueries.tabletAndBelow} {
    ${() =>
      isLoggedInRetailer()
        ? "transition: top 300ms cubic-bezier(0.175, 0.735, 0.680, 0.875);"
        : undefined};

    ${({ $isModal }) =>
      $isModal
        ? css`
            overflow: auto;
            padding: ${getSpacing(0, 12)};
            margin-bottom: ${getSpacing("6x")};
          `
        : undefined}
  }

  ${responsiveMediaQueries.mobile} {
    ${({ $isModal }) =>
      $isModal
        ? css`
            margin-bottom: ${getSpacing("2x")};
          `
        : undefined}
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- move align to a responsive prop
const HeaderContainer = styled(Row)<{
  $useUpdatedSpacing: boolean;
}>`
  padding-right: 3px;

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${({ $useUpdatedSpacing }) =>
      $useUpdatedSpacing ? getSpacing("2x", 0) : undefined};
    height: ${({ $useUpdatedSpacing }) =>
      $useUpdatedSpacing ? "40px" : undefined};
    align-items: ${({ $useUpdatedSpacing }) =>
      $useUpdatedSpacing ? "center" : undefined};
  }
`;

const ColumnWithPseudoElement = styled(Column)`
  background: ${Core.surface.inverse};

  &::after {
    content: "";
    position: absolute;
    top: -16px;
    left: 0;
    width: 100%;
    height: 16px;
    background: ${Core.surface.inverse};
  }
`;

const ScrollContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
  scrollbar-width: thin;
  scrollbar-color: ${Core.surface.subdued} ${Core.surface.inverse};
  &::-webkit-scrollbar {
    width: 3px;
    background-color: ${Core.surface.inverse};
  }
  &::-webkit-scrollbar-track {
    background-color: ${Core.surface.inverse};
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: ${Core.surface.subdued};
  }
`;

const ClearAllColumn = styled(Column)<{
  $useUpdatedSpacing: boolean;
}>`
  margin: ${({ $useUpdatedSpacing }) =>
    $useUpdatedSpacing ? undefined : "auto"};
  margin-left: auto;
  margin-right: ${getSpacing("2x")};
`;

const StyledToggleFilterRow = styled(ToggleFilterRow)`
  row-gap: ${getSpacing("1x")};
  padding-top: ${getSpacing("3x")};
  padding-bottom: ${getSpacing("3x")};
  border-bottom: 1px solid ${Core.border.muted};
`;

const StyledAlertBanner = styled(AlertBanner)`
  background-color: ${Core.surface.subdued};
  align-items: baseline;
  width: calc(100% - 16px);
  margin-top: ${getSpacing("3x")};
  margin-bottom: ${getSpacing("1x")};
`;
